import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Package, Car, PiggyBank, ArrowRight, CalendarDays, Banknote, Undo2,
  Calculator, TrendingDown, TrendingUp, AlertTriangle, CheckCircle2, Info,
  XCircle, User, Landmark, Heart, Clock, Users, CreditCard, Shield,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number, prefix = ''): string {
  if (v >= 0) return `${prefix}$${v.toLocaleString()}`;
  return `${prefix}-$${Math.abs(v).toLocaleString()}`;
}

const ICONS: Record<string, any> = {
  'package': Package, 'car': Car, 'piggy-bank': PiggyBank, 'arrow-right': ArrowRight,
  'calendar': CalendarDays, 'banknote': Banknote, 'undo': Undo2,
  'x-circle': XCircle, 'user': User, 'landmark': Landmark, 'heart': Heart,
  'clock': Clock, 'users': Users, 'credit-card': CreditCard, 'shield': Shield,
};

export default function ScenarioModellingPage() {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedScenarios, setSelectedScenarios] = useState<Array<{ type: string; amount: number }>>([]);
  const [customAmount, setCustomAmount] = useState<Record<string, number>>({});

  // Get clients
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-scenarios'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = clientsData?.clients || clientsData || [];

  // Get templates
  const { data: templates } = useQuery({
    queryKey: ['scenario-templates'],
    queryFn: async () => { const r = await apiClient.get('/scenarios/templates'); return r.data.data; },
  });

  // Calculate mutation
  const calcMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post('/scenarios/calculate', {
        clientId: selectedClientId,
        scenarios: selectedScenarios,
      });
      return r.data.data;
    },
  });

  const results = calcMutation.data?.results || [];
  const financials = calcMutation.data?.financials;

  const toggleScenario = (type: string, defaultAmount: number) => {
    const exists = selectedScenarios.find(s => s.type === type);
    if (exists) {
      setSelectedScenarios(prev => prev.filter(s => s.type !== type));
    } else {
      setSelectedScenarios(prev => [...prev, { type, amount: customAmount[type] || defaultAmount }]);
    }
  };

  const updateAmount = (type: string, amount: number) => {
    setCustomAmount(prev => ({ ...prev, [type]: amount }));
    setSelectedScenarios(prev => prev.map(s => s.type === type ? { ...s, amount } : s));
  };

  // Chart data
  const chartData = results.map((r: any) => ({
    name: r.scenario.length > 25 ? r.scenario.substring(0, 23) + '...' : r.scenario,
    saving: r.taxSaving,
    fill: r.taxSaving > 0 ? '#22c55e' : '#ef4444',
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Calculator}
        title="What-If Scenario Modelling"
        description="Calculate the tax impact of hypothetical decisions before June 30"
      />


      {/* Client Selector */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4">
            <Label className="whitespace-nowrap">Select Client:</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[350px]">
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.displayName || c.display_name} ({c.clientType || c.client_type || 'company'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Templates */}
      {selectedClientId && (
        <div>
          <h2 className="text-sm font-semibold mb-3">Select Scenarios to Model</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {(templates || []).map((t: any) => {
              const Icon = ICONS[t.icon] || Package;
              const isSelected = selectedScenarios.some(s => s.type === t.type);
              return (
                <Card
                  key={t.type}
                  className={cn('cursor-pointer transition-all hover:shadow-md', isSelected && 'border-primary ring-1 ring-primary')}
                  onClick={() => toggleScenario(t.type, t.defaultAmount)}
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0', isSelected ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground')}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{t.description}</p>
                        {isSelected && (
                          <div className="mt-2" onClick={e => e.stopPropagation()}>
                            <Label className="text-[10px]">Amount ($)</Label>
                            <Input
                              type="number"
                              className="h-7 text-xs mt-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={customAmount[t.type] || t.defaultAmount}
                              onChange={e => updateAmount(t.type, parseInt(e.target.value) || 0)}
                            />
                          </div>
                        )}
                      </div>
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {selectedScenarios.length > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={() => calcMutation.mutate()} disabled={calcMutation.isPending} className="gap-2">
                <Calculator className="h-4 w-4" />
                {calcMutation.isPending ? 'Calculating...' : `Calculate ${selectedScenarios.length} Scenario${selectedScenarios.length > 1 ? 's' : ''}`}
              </Button>
              <span className="text-xs text-muted-foreground">
                Total: ${selectedScenarios.reduce((s, sc) => s + sc.amount, 0).toLocaleString()}
              </span>
            </div>
          )}

          {calcMutation.isError && (
            <div className="mt-3 flex items-start gap-2 text-xs text-red-500">
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>Could not calculate scenarios. {(calcMutation.error as any)?.response?.data?.error || 'Please try again.'}</span>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <>
          {/* Current Financials */}
          {financials && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card><CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Income ({financials.fy})</p>
                <p className="text-sm font-bold">{fmtCurrency(financials.currentIncome || 0)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Expenses</p>
                <p className="text-sm font-bold">{fmtCurrency(financials.currentExpenses || 0)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Taxable Income</p>
                <p className="text-sm font-bold">{fmtCurrency(financials.currentTaxableIncome || 0)}</p>
              </CardContent></Card>
              <Card><CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-muted-foreground uppercase">Entity Type</p>
                <p className="text-sm font-bold capitalize">{calcMutation.data?.client?.entityType}</p>
              </CardContent></Card>
              <Card className="border-green-500/30"><CardContent className="pt-3 pb-3">
                <p className="text-[10px] text-green-500 uppercase">Total Tax Saving</p>
                <p className="text-lg font-bold text-green-500">
                  {fmtCurrency(calcMutation.data?.combinedSaving || results.reduce((s: number, r: any) => s + r.taxSaving, 0))}
                </p>
              </CardContent></Card>
            </div>
          )}

          {/* No accounting data -> figures (and the chart) compute against $0 income. */}
          {financials && (financials.currentIncome || 0) === 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-yellow-500" />
              <span className="text-yellow-700 dark:text-yellow-400">
                This client has no connected accounting data (Xero or QuickBooks), so savings are estimated against $0 income and the chart may look empty. Connect their accounting platform, or pick a client with Xero/QuickBooks linked, for accurate figures.
              </span>
            </div>
          )}

          {/* Tax Saving Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Tax Saving by Scenario</CardTitle>
              </CardHeader>
              <CardContent>
                {/* width="99%" (not 100%) is the recharts 2.5 workaround for the
                    ResponsiveContainer zero-width bug; text-muted-foreground feeds
                    currentColor so axes/grid adapt to light/dark. */}
                <div className="text-muted-foreground">
                  <ResponsiveContainer width="99%" height={Math.max(180, chartData.length * 50)}>
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.15} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: 'currentColor' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: 'currentColor' }} width={140} />
                      <Tooltip cursor={{ fill: 'currentColor', opacity: 0.06 }} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))', fontSize: 12 }} itemStyle={{ color: 'hsl(var(--foreground))' }} wrapperStyle={{ outline: 'none' }} formatter={(v: number) => [`${v >= 0 ? '$' : '-$'}${Math.abs(v).toLocaleString()}`, 'Tax Saving']} />
                      <Bar dataKey="saving" radius={[0, 4, 4, 0]} activeBar={false}>
                        {chartData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Detailed Results */}
          {results.map((result: any, i: number) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">{result.scenario}</CardTitle>
                  <Badge className={cn('text-xs', result.taxSaving > 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500')}>
                    {result.taxSaving > 0 ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
                    {fmtCurrency(Math.abs(result.taxSaving))} {result.taxSaving > 0 ? 'saving' : 'increase'}
                  </Badge>
                </div>
                <CardDescription className="text-xs">{result.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Comparison table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-3 py-1.5 text-left font-medium">Metric</th>
                        <th className="px-3 py-1.5 text-right font-medium">Before</th>
                        <th className="px-3 py-1.5 text-right font-medium">After</th>
                        <th className="px-3 py-1.5 text-right font-medium">Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-3 py-1.5">Taxable Income</td>
                        <td className="px-3 py-1.5 text-right">{fmtCurrency(result.currentTaxableIncome)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtCurrency(result.newTaxableIncome)}</td>
                        <td className="px-3 py-1.5 text-right text-blue-500">{fmtCurrency(-(result.currentTaxableIncome - result.newTaxableIncome))}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-1.5">Tax Payable</td>
                        <td className="px-3 py-1.5 text-right">{fmtCurrency(result.currentTax)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtCurrency(result.newTax)}</td>
                        <td className="px-3 py-1.5 text-right text-green-500">{fmtCurrency(-result.taxSaving)}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-1.5">Effective Saving Rate</td>
                        <td className="px-3 py-1.5 text-right" colSpan={2}></td>
                        <td className="px-3 py-1.5 text-right font-medium">{result.effectiveSavingRate}%</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1.5">Net Cash Flow Impact</td>
                        <td className="px-3 py-1.5 text-right" colSpan={2}></td>
                        <td className={cn('px-3 py-1.5 text-right font-medium', result.cashFlowImpact >= 0 ? 'text-green-500' : 'text-red-500')}>
                          {result.cashFlowImpact >= 0 ? '+$' : '-$'}{Math.abs(result.cashFlowImpact).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Notes */}
                {result.notes.length > 0 && (
                  <div className="space-y-1">
                    {result.notes.map((note: string, j: number) => (
                      <div key={j} className="flex items-start gap-2 text-xs">
                        <Info className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{note}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Warnings */}
                {result.warnings.length > 0 && (
                  <div className="space-y-1">
                    {result.warnings.map((warn: string, j: number) => (
                      <div key={j} className="flex items-start gap-2 text-xs">
                        <AlertTriangle className="h-3 w-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-yellow-600 dark:text-yellow-400">{warn}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* ATO Rules */}
                {result.atoRules.length > 0 && (
                  <div className="bg-muted/30 rounded-lg p-2 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase">ATO Rules</p>
                    {result.atoRules.map((rule: string, j: number) => (
                      <p key={j} className="text-[10px] text-muted-foreground">• {rule}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}
